import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	SAPClaimSocialStarterPetitionAngles,
	type SapClaimSocialStarterPetitionAnglesMutation,
	type SapClaimSocialStarterPetitionAnglesMutationVariables,
} from './claimSocialStarterPetitionAngles.graphql';

type Params = SapClaimSocialStarterPetitionAnglesMutationVariables['input'] & { utilityContext: UtilityContext };

// The caller awaits this before navigating to starter onboarding (to avoid racing the server-side
// staffing it triggers), so a hung request must reject rather than block publish indefinitely.
const CLAIM_TIMEOUT_MS = 8000;

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	// A timer-backed rejection is the standard timeout primitive; the timer is cleared in `finally`
	// when the real promise wins, so it never lingers.
	// oxlint-disable-next-line promise/avoid-new
	const timeout = new Promise<never>((_resolve, reject) => {
		timer = setTimeout(() => reject(new Error(`Claim social starter petition angle timed out after ${ms}ms`)), ms);
	});
	try {
		return await Promise.race([promise, timeout]);
	} finally {
		clearTimeout(timer);
	}
}

// Best-effort link to the already-created petition; failures are only logged, never surfaced or blocking.
export async function claimSocialStarterPetitionAngles({ token, petitionId, utilityContext }: Params): Promise<void> {
	const {
		errorReporter,
		gql: { fetch },
	} = utilityContext;

	let requestId: string | undefined;
	let reason: string | undefined;

	try {
		const {
			data,
			errors,
			requestId: fetchRequestId,
		} = await withTimeout(
			fetch<SapClaimSocialStarterPetitionAnglesMutation, SapClaimSocialStarterPetitionAnglesMutationVariables>({
				query: SAPClaimSocialStarterPetitionAngles,
				variables: { input: { token, petitionId } },
				rejectOnError: false,
			}),
			CLAIM_TIMEOUT_MS,
		);
		requestId = fetchRequestId;

		if (errors?.length) throw new Error(errors.map((error) => error.message).join('; '));
		if (!data) throw new Error('No data returned from mutation.');

		const { success, reason: failureReason } = data.claimSocialStarterPetitionAngles;
		if (!success) {
			reason = failureReason ?? undefined;
			throw new Error('Failed to claim social starter petition angle');
		}
	} catch (error) {
		void errorReporter.report({
			error: error instanceof Error ? error : new Error('Failed to claim social starter petition angle'),
			context: { requestId, reason },
		});
	}
}
